import { CommonModule } from "@angular/common";
import { Component, EventEmitter, Input, Output, ViewChild } from "@angular/core";
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { TranslateModule } from "@ngx-translate/core";
import {
    FILE_EXTENSION_REGEX,
    FILE_TYPE_DEFAULT_ICON_CLASS,
    GENERIC_CATEGORY_LIST_TRANSLATE_BASE,
    MAX_FILE_LENGTH,
    MAX_FILE_SIZE,
    STORED_DOCUMENTS_VIEWCHILD_UPLOAD,
} from "@shared/constants/stored-documents.constants";
import { FILENAME_VALID_CHARACTERS } from "@shared/constants/validators.constants";
import { StoredDocumentValidType } from "@shared/enums/stored-document-valid-type";
import { StoredDocumentCategory } from "@shared/models/stored-document-category.model";
import { StoredDocumentCreationForm } from "@shared/models/stored-document-creation-form.model";
import { StoredDocumentsInformations } from "@shared/models/stored-documents-informations.model";
import { createRegexValidator } from "@shared/validators/common.validator";
import { MessageService } from "primeng/api";
import { ButtonModule } from "primeng/button";
import { CardModule } from "primeng/card";
import { DialogModule } from "primeng/dialog";
import { DropdownModule } from "primeng/dropdown";
import { FileUpload, FileUploadModule } from "primeng/fileupload";
import { InputTextModule } from "primeng/inputtext";
import { OverlayPanelModule } from "primeng/overlaypanel";
import { ProgressBarModule } from "primeng/progressbar";
import { SkeletonModule } from "primeng/skeleton";

@Component({
  selector: "app-stored-documents-upload",
  standalone: true,
  imports: [
    ButtonModule,
    CardModule,
    CommonModule,
    FileUploadModule,
    OverlayPanelModule,
    ProgressBarModule,
    InputTextModule,
    SkeletonModule,
    TranslateModule,
    DialogModule,
    ReactiveFormsModule,
    DropdownModule,
  ],
  providers: [MessageService],
  templateUrl: "./stored-documents-upload.component.html",
  styleUrl: "./stored-documents-upload.component.scss",
})
export class StoredDocumentsUploadComponent {
  @Input() documentsInformations: StoredDocumentsInformations | undefined;
  @Input() categories: StoredDocumentCategory[] | undefined;
  @Output() validateUploadFile = new EventEmitter<StoredDocumentCreationForm>();

  @ViewChild(STORED_DOCUMENTS_VIEWCHILD_UPLOAD) documentUpload!: FileUpload;

  fileNameWithoutExtension = "";
  displayErrorDialog = false;
  showStorageError = false;

  addFileForm!: FormGroup;
  documentCreationForm: StoredDocumentCreationForm | undefined;

  constructor(public messageService: MessageService) {
    this.addFileForm = new FormGroup({
      fileNameControlName: new FormControl("", [Validators.required, createRegexValidator(FILENAME_VALID_CHARACTERS)]),
      fileCategory: new FormControl("", Validators.required),
    });
    this.addFileForm.markAllAsTouched();
  }

  documentUploadChooseFile() {
    this.documentUpload?.choose();
  }

  onFileSelect(event: any) {
    const file: File = event.files[0];
    if (file && this.documentsInformations) {
      const validTypes = Object.values(StoredDocumentValidType);
      if (!validTypes.includes(file.type as StoredDocumentValidType)) {
        this.displayErrorDialog = true;
        return;
      }

      const maxSize = MAX_FILE_SIZE;
      const totalFilesSize = this.documentsInformations.storedDocuments.reduce(
        (totalSize, doc) => totalSize + doc.size,
        0
      );
      if (file.size + totalFilesSize > maxSize) {
        this.documentUpload?.clear();
        this.displayErrorDialog = true;
        this.showStorageError = true;
        return;
      }

      this.addFileForm.setValue({
        fileNameControlName: this.removeFileExtension(file.name).substring(0, MAX_FILE_LENGTH),
        fileCategory: "",
      });
    }
  }

  get fileIcon(): string {
    return FILE_TYPE_DEFAULT_ICON_CLASS;
  }

  close() {
    this.displayErrorDialog = false;
  }

  getGenericCategoryTranslationUri(id: string) {
    return GENERIC_CATEGORY_LIST_TRANSLATE_BASE + id;
  }

  removeFileExtension(fileName: string): string {
    return fileName.replace(FILE_EXTENSION_REGEX, "");
  }

  closeUpload() {
    this.documentUpload?.clear();
  }

  saveFile() {
    const file: File = this.documentUpload.files[0];
    this.documentCreationForm = {
      categoryId: this.addFileForm.value.fileCategory.id,
      name: this.addFileForm.value.fileNameControlName,
      file: file,
    };
    this.validateUploadFile.emit(this.documentCreationForm);
  }

  clear() {
    this.documentUpload.clear();
  }
}
